import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LIMITS, SETTINGS_ROLES, formatSequence, type Ward } from '@mahallu/shared';
import { ApiError, loadErrorKey, register, useMe, useSequences, useWards } from '../api';
import { FormFooter, FormSection, Modal, TextField } from '../components/Form';
import { Icon } from '../components/Icon';
import { PageHeader } from '../components/Parts';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

function WardForm({ ward, onClose }: { ward?: Ward; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState(ward?.code ?? '');
  const [nameEn, setNameEn] = useState(ward?.nameEn ?? '');
  const [nameMl, setNameMl] = useState(ward?.nameMl ?? '');
  const save = useMutation({
    mutationFn: () =>
      ward
        ? register.updateWard(ward.code, { nameEn: nameEn.trim(), nameMl: nameMl.trim() || null })
        : register.createWard({ code: code.trim().toUpperCase(), nameEn: nameEn.trim(), nameMl: nameMl.trim() || null }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ['wards'] }), qc.invalidateQueries({ queryKey: ['sequences'] })]);
      onClose();
    },
  });
  return (
    <Modal
      title={ward ? `${t('wards.rename')} · ${ward.code}` : t('wards.add')}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={ward ? t('form.save') : t('wards.add')} error={save.isError ? errorText(save.error) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormSection title={t('wards.section')}>
          <TextField label={t('wards.code')} value={code} onChange={(v) => setCode(v.toUpperCase())} maxLength={LIMITS.wardCode} required disabled={Boolean(ward)} hint={ward ? t('wards.codeFixed') : t('wards.codeHint')} />
          <TextField label={t('wards.nameEn')} value={nameEn} onChange={setNameEn} maxLength={LIMITS.name} required />
          <TextField label={t('wards.nameMl')} value={nameMl} onChange={setNameMl} maxLength={LIMITS.name} lang="ml" full />
        </FormSection>
      </form>
    </Modal>
  );
}

/** Register › Wards & Areas: the areas inside the mahallu, each numbering its own houses. */
export function Wards() {
  const { t } = useTranslation();
  const me = useMe();
  const wards = useWards();
  const seqs = useSequences();
  const [editing, setEditing] = useState<Ward | 'new' | null>(null);
  const canEdit = Boolean(me.data?.role && SETTINGS_ROLES.includes(me.data.role));
  const houseSeq = seqs.data?.find((s) => s.code === 'HOUSE');

  return (
    <>
      <PageHeader
        icon="map"
        title={t('wards.title')}
        desc={t('wards.desc')}
        actions={canEdit ? [{ label: t('wards.add'), icon: 'plus', primary: true, onClick: () => setEditing('new') }] : []}
      />
      {wards.isError ? <p className="form-error">{t(loadErrorKey(wards.error))}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('wards.code')}</th>
                <th>{t('wards.nameEn')}</th>
                <th>{t('wards.nameMl')}</th>
                <th>{t('wards.nextHouse')}</th>
                {canEdit ? <th>{t('col.actions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {wards.isLoading ? (
                <tr>
                  <td colSpan={5} className="tbl-msg">
                    {t('table.loading')}
                  </td>
                </tr>
              ) : null}
              {wards.data?.map((w) => {
                const next = houseSeq?.next.find((n) => n.scopeKey === w.code)?.nextValue ?? 1;
                return (
                  <tr key={w.id}>
                    <td className="num">
                      <b>{w.code}</b>
                    </td>
                    <td>{w.nameEn}</td>
                    <td className="ml">{w.nameMl ?? '—'}</td>
                    <td className="num seq-number">{houseSeq ? formatSequence(houseSeq.pattern, next, { at: new Date(), ward: w.code }) : '—'}</td>
                    {canEdit ? (
                      <td>
                        <button type="button" className="icon-btn" title={t('wards.rename')} aria-label={`${t('wards.rename')} ${w.code}`} onClick={() => setEditing(w)}>
                          <Icon name="edit" />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {editing ? <WardForm ward={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}
